import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Space, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  fundExceptionsApi,
  type FundExceptionAction,
  type FundExceptionDetail,
} from "../../api/fund-exceptions.api";
import { DetailDrawer } from "../../components/DetailDrawer";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { FundExceptionResolveModal } from "./FundExceptionResolveModal";
import {
  ACTION_RESULT_LABEL,
  actionValueText,
  entitlementStatusLabel,
  exceptionTypeLabel,
  formatDateTime,
  localStatusLabel,
  recommendedActionLabel,
  STATUS_META,
} from "./fund-exception-labels";

export interface FundExceptionDetailDrawerProps {
  exceptionId: string;
  canResolve: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}

const actionColumns: ColumnsType<FundExceptionAction> = [
  {
    title: "操作人",
    dataIndex: "operatorId",
    width: 120,
    render: (value) => value ?? "-",
  },
  {
    title: "动作",
    dataIndex: "action",
    width: 110,
    render: (value: FundExceptionAction["action"]) =>
      recommendedActionLabel(value),
  },
  {
    title: "业务原因",
    dataIndex: "reason",
    ellipsis: true,
    render: (value) => value ?? "-",
  },
  {
    title: "结果",
    dataIndex: "result",
    width: 90,
    render: (value: FundExceptionAction["result"]) => {
      const color =
        value === "SUCCEEDED"
          ? "green"
          : value === "FAILED"
            ? "red"
            : "default";
      return <Tag color={color}>{ACTION_RESULT_LABEL[value] ?? value}</Tag>;
    },
  },
  {
    title: "处理前值",
    dataIndex: "beforeValue",
    width: 180,
    ellipsis: true,
    render: (value: Record<string, unknown> | null) => actionValueText(value),
  },
  {
    title: "处理后值",
    dataIndex: "afterValue",
    width: 180,
    ellipsis: true,
    render: (value: Record<string, unknown> | null) => actionValueText(value),
  },
  {
    title: "处理时间",
    dataIndex: "performedAt",
    width: 140,
    render: (value: string) => formatDateTime(value),
  },
];

/** 差错单详情抽屉：渠道证据、双侧状态、本地引用与审计轨迹同屏展示。 */
export function FundExceptionDetailDrawer({
  exceptionId,
  canResolve,
  onClose,
  onChanged,
}: FundExceptionDetailDrawerProps) {
  const [detail, setDetail] = useState<FundExceptionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await fundExceptionsApi.getFundException(exceptionId));
    } catch (error) {
      setDetail(null);
      message.error(
        error instanceof Error ? error.message : "资金异常详情加载失败",
      );
    } finally {
      setLoading(false);
    }
  }, [exceptionId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const evidenceRows = detail
    ? [
        {
          key: "channel",
          side: "渠道侧",
          status: localStatusLabel(detail.channelStatus),
          amount: (
            <MoneyDisplay
              amountInCents={detail.channelEvidence.channelAmountMinor}
            />
          ),
          reference: detail.channelEvidence.platformTxnId,
        },
        {
          key: "local",
          side: "本地侧",
          status: localStatusLabel(detail.localStatus),
          amount:
            detail.localEvidenceRefs.localAmountMinor === null ? (
              "-"
            ) : (
              <MoneyDisplay
                amountInCents={detail.localEvidenceRefs.localAmountMinor}
              />
            ),
          reference: detail.localEvidenceRefs.paymentTransactionId ?? "-",
        },
      ]
    : [];

  const handleResolved = useCallback(async () => {
    await Promise.all([loadDetail(), onChanged()]);
  }, [loadDetail, onChanged]);

  const showResolveEntry =
    canResolve && (detail?.status === "PENDING" || detail?.status === "FAILED");

  return (
    <DetailDrawer
      open
      width={900}
      loading={loading}
      title={detail ? `资金异常详情 · ${detail.exceptionNo}` : "资金异常详情"}
      onClose={onClose}
      extra={
        detail && showResolveEntry ? (
          <Button
            type="primary"
            onClick={() => setResolving(true)}
            data-testid="fund-exception-resolve-entry"
          >
            处理
          </Button>
        ) : undefined
      }
    >
      {detail && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {!canResolve && (
            <Alert
              type="info"
              showIcon
              message="当前账号无资金异常处理权限"
              data-testid="fund-exception-permission-alert"
            />
          )}
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="异常编号">
              {detail.exceptionNo}
            </Descriptions.Item>
            <Descriptions.Item label="异常类型">
              {exceptionTypeLabel(detail.exceptionType)}
            </Descriptions.Item>
            <Descriptions.Item label="订单号">
              {detail.orderNo ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="学生">
              {detail.studentName ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="异常金额">
              <MoneyDisplay amountInCents={detail.differenceAmountMinor} />
            </Descriptions.Item>
            <Descriptions.Item label="发现时间">
              {formatDateTime(detail.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="推荐动作">
              {recommendedActionLabel(detail.recommendedAction)}
            </Descriptions.Item>
            <Descriptions.Item label="处理状态">
              <Tag color={STATUS_META[detail.status]?.color}>
                {STATUS_META[detail.status]?.label ?? detail.status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <Descriptions title="渠道原始事件" bordered size="small" column={2}>
            <Descriptions.Item label="平台交易 ID">
              {detail.channelEvidence.platformTxnId}
            </Descriptions.Item>
            <Descriptions.Item label="outTradeNo">
              {detail.channelEvidence.outTradeNo ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="渠道金额">
              <MoneyDisplay
                amountInCents={detail.channelEvidence.channelAmountMinor}
              />
            </Descriptions.Item>
            <Descriptions.Item label="账单日">
              {detail.channelEvidence.billDate}
            </Descriptions.Item>
            <Descriptions.Item label="完成时间">
              {formatDateTime(detail.channelEvidence.completedAt)}
            </Descriptions.Item>
            <Descriptions.Item label="支付方式">
              {detail.channelEvidence.paymentMethod ?? "-"}
            </Descriptions.Item>
          </Descriptions>
          <Table
            size="small"
            rowKey="key"
            columns={[
              { title: "证据侧", dataIndex: "side" },
              { title: "状态", dataIndex: "status" },
              { title: "金额", dataIndex: "amount" },
              { title: "流水 / 引用", dataIndex: "reference" },
            ]}
            dataSource={evidenceRows}
            pagination={false}
            data-testid="fund-exception-status-comparison"
          />
          <Descriptions title="本地证据引用" bordered size="small" column={2}>
            <Descriptions.Item label="支付流水 ID">
              {detail.localEvidenceRefs.paymentTransactionId ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="原始事件 ID">
              {detail.localEvidenceRefs.rawPaymentEventId ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="支付会话 ID">
              {detail.localEvidenceRefs.wechatPaymentSessionId ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="订单状态">
              {detail.localEvidenceRefs.orderStatus
                ? localStatusLabel(detail.localEvidenceRefs.orderStatus)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="原始事件状态">
              {detail.localEvidenceRefs.rawEventStatus
                ? localStatusLabel(detail.localEvidenceRefs.rawEventStatus)
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="课时开通状态">
              {entitlementStatusLabel(detail)}
            </Descriptions.Item>
          </Descriptions>
          <Table
            size="small"
            rowKey="actionId"
            columns={actionColumns}
            dataSource={detail.actions}
            pagination={false}
            scroll={{ x: "max-content" }}
            data-testid="fund-exception-action-table"
          />
        </Space>
      )}
      {detail && (
        <FundExceptionResolveModal
          open={resolving}
          exception={detail}
          onClose={() => setResolving(false)}
          onResolved={handleResolved}
        />
      )}
    </DetailDrawer>
  );
}
